import type { IOrganizationMember } from '@/domain/entities/org-member';
import type { IBusinessUser } from '@/domain/entities/user';
import { RoleScopeType } from '@/domain/entities/role';
import { restoreSession } from '@/features/auth/authThunks';
import { bootstrapUserProfile } from '@/features/users/usersThunks';
import { setNeedsOnboarding } from '@/features/users/usersSlice';
import { fetchOrganization, fetchUserMemberships } from '@/features/organizations/organizationsThunks';
import { setActiveOrganizationId, upsertOrganization } from '@/features/organizations/organizationsSlice';
import { fetchRoles } from '@/features/roles/rolesThunks';
import { isPhoneVerified, isProfileComplete } from '@/features/users/onboardingSelectors';
import { OrgStatus } from '@/domain/shared/shared';
import type { StoredSessionData } from '@/services/auth/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { isFixedOrgRoleKey } from '@/lib/fixedRoles';
import { setBootstrapping, setBootstrapError, setBootstrapPhase } from './appStateSlice';
import { BootstrapPhase } from './appStateTypes';

function toErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return fallback;
}

export const runBootstrap = createAppAsyncThunk<void, void, { rejectValue: { message: string } }>(
  'appState/runBootstrap',
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(setBootstrapping(true));
    dispatch(setBootstrapPhase({ phase: BootstrapPhase.RestoringSession, message: 'Restoring your session…' }));

    try {
      let session: StoredSessionData;
      try {
        session = await dispatch(restoreSession()).unwrap();
      } catch (error) {
        const message = toErrorMessage(error, 'Failed to restore session');
        dispatch(setBootstrapError(message));
        return rejectWithValue({ message });
      }

      if (!session.accessToken || !session.userId) {
        dispatch(setNeedsOnboarding(false));
        dispatch(setActiveOrganizationId(null));
        dispatch(setBootstrapPhase({ phase: BootstrapPhase.Unauthenticated, message: null }));
        return;
      }

      dispatch(setBootstrapPhase({ phase: BootstrapPhase.LoadingProfile, message: 'Loading your profile…' }));

      let profile: IBusinessUser;
      try {
        profile = await dispatch(bootstrapUserProfile()).unwrap();
      } catch (error) {
        const message = toErrorMessage(error, 'Failed to bootstrap profile');
        dispatch(setBootstrapError(message));
        return rejectWithValue({ message });
      }

      dispatch(
        setBootstrapPhase({
          phase: BootstrapPhase.LoadingMemberships,
          message: 'Loading your organization memberships…',
        }),
      );

      let memberships: IOrganizationMember[] = [];
      try {
        memberships = await dispatch(fetchUserMemberships({ memberID: profile.applicationUserId })).unwrap();
      } catch (error) {
        const message = toErrorMessage(error, 'Failed to load memberships');
        dispatch(setBootstrapError(message));
        return rejectWithValue({ message });
      }

      const activeMemberships = memberships.filter(
        (membership) => membership.status === OrgStatus.Active || membership.status === OrgStatus.Inactive,
      );
      const selectedMembership =
        activeMemberships.find((membership) => membership.id === profile?.organizationID) ?? activeMemberships[0];
      const activeOrganizationId = selectedMembership?.id ?? null;
      const hasActiveMembership = activeMemberships.length > 0;

      dispatch(setActiveOrganizationId(activeOrganizationId));
      dispatch(
        setBootstrapPhase({
          phase: BootstrapPhase.EvaluatingOnboarding,
          message: 'Checking onboarding requirements…',
        }),
      );

      const needsProfileOrPhone = !profile || !isProfileComplete(profile) || !isPhoneVerified(profile);
      const needsOnboarding = needsProfileOrPhone || !hasActiveMembership;

      dispatch(setNeedsOnboarding(needsOnboarding));

      if (needsOnboarding) {
        dispatch(
          setBootstrapPhase({
            phase: BootstrapPhase.OnboardingRequired,
            message: 'Onboarding is required to continue.',
          }),
        );
        return;
      }

      const roleKey = selectedMembership?.roleKey?.trim();
      if (activeOrganizationId && roleKey && !isFixedOrgRoleKey(roleKey)) {
        dispatch(setBootstrapPhase({ phase: BootstrapPhase.LoadingRole, message: 'Loading your role…' }));
        try {
          await dispatch(fetchRoles({ scopeType: RoleScopeType.ORG, scopeID: activeOrganizationId, roleKey })).unwrap();
        } catch (error) {
          const message = toErrorMessage(error, 'Failed to load role');
          dispatch(setBootstrapError(message));
          return rejectWithValue({ message });
        }
      }

      if (activeOrganizationId) {
        dispatch(
          setBootstrapPhase({ phase: BootstrapPhase.LoadingOrganization, message: 'Loading your organization…' }),
        );
        try {
          const organization = await dispatch(fetchOrganization({ orgId: activeOrganizationId })).unwrap();
          dispatch(upsertOrganization(organization));
        } catch (error) {
          const message = toErrorMessage(error, 'Failed to load organization');
          dispatch(setBootstrapError(message));
          return rejectWithValue({ message });
        }
      }

      dispatch(setBootstrapPhase({ phase: BootstrapPhase.Ready, message: null }));
    } finally {
      dispatch(setBootstrapping(false));
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState().appState.isBootstrapping;
    },
  },
);
